import { apiFetch } from '../../utils/apiFetch';
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useHybridAuth } from '../../contexts/HybridAuthContext';
import { useWhitelabel } from '../../contexts/WhitelabelContext';
import { toast } from 'react-hot-toast';

interface BetaStatus {
  beta_enabled: boolean;
  welcome_message: string;
  description: string;
  coming_soon_message: string;
}

export default function BetaSignup() {
  const navigate = useNavigate();
  const { refreshUser } = useHybridAuth();
  const { branding } = useWhitelabel();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [betaStatus, setBetaStatus] = useState<BetaStatus | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(true);

  useEffect(() => {
    checkBetaStatus();
  }, []);

  const checkBetaStatus = async () => {
    try {
      const response = await apiFetch('/api/beta/status', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setBetaStatus(data);
      }
    } catch (error) {
      console.error('Failed to check beta status:', error);
      toast.error('Unable to load signup page');
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (password !== passwordConfirmation) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      // Submit registration
      const response = await apiFetch('/api/beta/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          email,
          password,
          password_confirmation: passwordConfirmation,
          company_name: companyName || undefined,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || `Welcome to ${branding.companyName}!`);

        // Refresh user context
        await refreshUser();

        // Redirect to dashboard
        navigate('/dashboard');
      } else {
        const error = await response.json();

        if (error.errors) {
          // Handle validation errors
          const firstError = Object.values(error.errors)[0];
          toast.error(Array.isArray(firstError) ? firstError[0] : String(firstError));
        } else {
          toast.error(error.message || 'Signup failed. Please try again.');
        }
      }
    } catch (error) {
      console.error('Signup error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  const isBetaEnabled = betaStatus?.beta_enabled ?? true;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex flex-col items-center justify-center mb-6">
            {branding.hasWhitelabel && branding.companyLogo ? (
              <>
                <img
                  className="max-h-20 max-w-[300px] object-contain mb-4"
                  src={branding.companyLogo}
                  alt={branding.companyName}
                  style={{ maxHeight: '80px', maxWidth: '300px' }}
                />
                <h2 className="text-2xl font-bold text-white text-center">
                  {branding.companyName}
                </h2>
              </>
            ) : (
              <>
                <img
                  className="h-16 w-auto mb-3"
                  src="/assets/img/optimancer_logo.svg"
                  alt={branding.companyName}
                />
                <h2 className="text-2xl font-bold text-white text-center">
                  {branding.companyName}
                </h2>
              </>
            )}
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            {isBetaEnabled
              ? (betaStatus?.welcome_message || `Join the ${branding.companyName} Beta`)
              : 'Sign Up Now!'}
          </h2>
          <p className="mt-2 text-center text-sm text-slate-400">
            {isBetaEnabled ? (
              betaStatus?.description || 'Get early access to our AI-powered AIO analysis platform.'
            ) : (
              <a
                href="https://dominators.internetdominators.com/optimancerbf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline"
              >
                Click here to sign up Now!
              </a>
            )}
          </p>
        </div>

        {isBetaEnabled ? (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="rounded-md shadow-sm space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="first-name" className="sr-only">
                    First Name
                  </label>
                  <input
                    id="first-name"
                    name="first_name"
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="appearance-none relative block w-full px-3 py-2 border border-slate-700 placeholder-slate-500 text-white bg-slate-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="First Name"
                  />
                </div>
                <div>
                  <label htmlFor="last-name" className="sr-only">
                    Last Name
                  </label>
                  <input
                    id="last-name"
                    name="last_name"
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="appearance-none relative block w-full px-3 py-2 border border-slate-700 placeholder-slate-500 text-white bg-slate-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Last Name"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="sr-only">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-2 border border-slate-700 placeholder-slate-500 text-white bg-slate-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Email address"
                />
              </div>

              <div>
                <label htmlFor="company-name" className="sr-only">
                  Company Name (Optional)
                </label>
                <input
                  id="company-name"
                  name="company_name"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-2 border border-slate-700 placeholder-slate-500 text-white bg-slate-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Company Name (Optional)"
                />
              </div>

              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-2 border border-slate-700 placeholder-slate-500 text-white bg-slate-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Password (min. 8 characters)"
                />
              </div>

              <div>
                <label htmlFor="password-confirmation" className="sr-only">
                  Confirm Password
                </label>
                <input
                  id="password-confirmation"
                  name="password_confirmation"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-2 border border-slate-700 placeholder-slate-500 text-white bg-slate-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Confirm Password"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Creating account...' : 'Create Beta Account'}
              </button>
            </div>

            <div className="text-center">
              <p className="text-sm text-slate-400">
                Already have an account?{' '}
                <Link to="/sign-in" className="font-medium text-blue-400 hover:text-blue-300">
                  Sign in
                </Link>
              </p>
            </div>
          </form>
        ) : (
          <div className="mt-8 text-center">
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-8">
              <svg
                className="mx-auto h-12 w-12 text-slate-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <p className="mt-4 text-slate-300">
                Beta signups are currently closed
              </p>
              <Link
                to="/sign-in"
                className="mt-6 inline-block px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Go to Sign In
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
